// The team's door.
//
// An unauthenticated visitor gets the static SPA shell like anyone else; it calls
// bootstrap, receives 401, and lands here. The advantage of doing it this way is
// that the door is branded from the same data as the product: the brand route is
// readable signed-out precisely so this screen can show the team's name and
// colour without a second rendering path in the Worker.
//
// **Two ways in, one email.** The link is one tap. The code exists because the link
// cannot work at all in an installed app on iOS — a Home Screen app has its own
// cookie jar, so a link opened from Mail signs Safari in and leaves the app exactly
// where it was. So the screen that asked for the email also accepts the code, which
// keeps the session in the window the person is actually looking at.
//
// Whether an address is on the roster is not something the server will say, so this
// screen says the same thing whatever was typed.
//
// **The door you have already got a key to.** Arriving here does not always mean
// signed out: it also means signed in as an address with no seat HERE, while a
// second identity on the same device has one. That was indistinguishable from
// being a stranger, and the screen's answer was to mail a link re-proving an
// address the device had proved already. So when `hop` says another identity on
// this device can open this workspace, the way in is one tap and the form is the
// second option — see lib/identities.ts.

import { useState } from 'react'

import { api, type PublicInfo } from '../lib/api'
import { displayName } from '../lib/brand'
import { goAs, type Reachable } from '../lib/identities'
import { doorButton, DoorFinePrint, DoorHeading, DoorLabel, doorField, Doorway } from './Doorway'

export function SignIn({
  info,
  hop,
  onSignedIn,
}: {
  info: PublicInfo
  /** Another identity on this device with a seat here, if there is one. */
  hop?: Reachable | null
  onSignedIn: () => void
}) {
  const { brand, guestAccess } = info
  const name = displayName(brand)
  const expired = new URLSearchParams(location.search).get('expired') === '1'
  // A hop with no token is the ACTIVE identity, which bootstrap has just refused:
  // the membership projection is behind the log. Offering it would loop.
  const other = hop?.token ? hop : null

  return (
    <Doorway
      brand={brand}
      wordmark={name}
      headline={
        <>Track what you are building, and show it to the people you are building it for.</>
      }
      lede={
        guestAccess ? (
          <>
            Everything here is invented and it resets every night. Put in a name and look around.
            The records you make will carry it.
          </>
        ) : (
          brand.loginBlurb || (
            <>
              One record of the work. Each client gets a page showing the parts of it you marked
              visible to them, built from the same records you work in.
            </>
          )
        )
      }
    >
      {expired ? (
        <p
          className="rounded-control note mb-5 border px-3 py-2 leading-relaxed"
          style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}
        >
          That link has expired or has already been used. Ask for another.
        </p>
      ) : null}

      {other ? (
        <AlreadyIn hop={other} guestAccess={guestAccess} onSignedIn={onSignedIn} />
      ) : guestAccess ? (
        <GuestForm onSignedIn={onSignedIn} />
      ) : (
        <EmailForm onSignedIn={onSignedIn} />
      )}
    </Doorway>
  )
}

/**
 * The way in for somebody this device has already proved.
 *
 * One tap: `set-active` swaps which of the device's sessions is the live one and
 * the page reloads into the workspace. No letter, because nothing here needs
 * proving — the session was proved when it was created and Better Auth still
 * holds it. The address is named on the button, because becoming somebody else is
 * not something to do to a person silently.
 */
function AlreadyIn({
  hop,
  guestAccess,
  onSignedIn,
}: {
  hop: Reachable
  guestAccess: boolean
  onSignedIn: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState(false)

  if (form) {
    return guestAccess ? (
      <GuestForm onSignedIn={onSignedIn} />
    ) : (
      <EmailForm onSignedIn={onSignedIn} />
    )
  }

  return (
    <div className="flex flex-col">
      <DoorHeading note={<>You are already signed in on this device as {hop.via}.</>}>
        Continue
      </DoorHeading>
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          setBusy(true)
          void goAs(hop.token, location.pathname + location.search)
        }}
        className="rounded-control text-body mt-6 w-full px-3 py-2.5 font-semibold disabled:opacity-50"
        style={doorButton}
      >
        {busy ? 'Opening…' : `Continue as ${hop.via}`}
      </button>
      <button
        type="button"
        onClick={() => setForm(true)}
        className="meta mt-4 self-start underline underline-offset-2"
      >
        use a different address
      </button>
      <DoorFinePrint>
        Both addresses stay signed in on this device; the account menu switches back.
      </DoorFinePrint>
    </div>
  )
}

/** An email, then the code from it. The only door on a real workspace. */
function EmailForm({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function ask(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await api.requestCode(email.trim())
      setSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not send.')
    } finally {
      setBusy(false)
    }
  }

  async function redeem(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await api.submitCode(email.trim(), code.trim())
      onSignedIn()
    } catch {
      // The library refuses a wrong or spent code the same way; there is nothing
      // more specific worth saying, and guessing at "expired" would often be wrong.
      setError('That code did not work. Check the newest email, or ask for another.')
      setBusy(false)
    }
  }

  if (sent) {
    return (
      <form onSubmit={redeem} className="flex flex-col">
        <DoorHeading note={<>Open it and tap the button, or type the code below.</>}>
          Check your email
        </DoorHeading>
        <p className="text-body mt-2.5 mb-6 leading-relaxed">
          If <b>{email.trim()}</b> is on the team, it is on its way.
        </p>

        <DoorLabel htmlFor="code">Code from the email</DoorLabel>
        <input
          id="code"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="123456"
          autoFocus
          className="num rounded-control text-title w-full border px-3 py-2.5 text-center outline-none"
          style={{ ...doorField, letterSpacing: '0.3em' }}
        />
        <button
          type="submit"
          disabled={busy || code.length < 6}
          className="rounded-control text-body mt-2.5 w-full px-3 py-2.5 font-semibold disabled:opacity-50"
          style={doorButton}
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        {error ? (
          <p className="text-meta mt-3" style={{ color: 'var(--crit)' }}>
            {error}
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => {
            setSent(false)
            setCode('')
            setError('')
          }}
          className="meta mt-4 self-start underline underline-offset-2"
        >
          use a different address
        </button>
      </form>
    )
  }

  return (
    <form onSubmit={ask} className="flex flex-col">
      <DoorHeading
        note={
          <>
            Your team keeps the list of who can sign in. There is no password — the email carries a
            link and a code, and either one opens it.
          </>
        }
      >
        Sign in
      </DoorHeading>

      <div className="mt-6">
        <DoorLabel htmlFor="email">Email address</DoorLabel>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@yourcompany.com"
          autoComplete="email"
          autoFocus
          className="rounded-control text-lead w-full border px-3 py-2.5 outline-none"
          style={doorField}
        />
      </div>
      <button
        type="submit"
        disabled={busy || !email.includes('@')}
        className="rounded-control text-body mt-2.5 w-full px-3 py-2.5 font-semibold disabled:opacity-50"
        style={doorButton}
      >
        {busy ? 'Sending…' : 'Email me a sign-in link'}
      </button>
      {error ? (
        <p className="text-meta mt-3" style={{ color: 'var(--crit)' }}>
          {error}
        </p>
      ) : null}
      <DoorFinePrint>
        Links are sent only to addresses already on the team's People list. Being on it is how
        access is granted, and coming off it is how it is taken away.
      </DoorFinePrint>
    </form>
  )
}

/**
 * The demo's door: a name, and in you go.
 *
 * There is no password because there is nothing to protect: the data is fictional
 * and it is wiped nightly. The name is asked for anyway, and it is not a formality:
 * crum's whole argument is that a person is answerable for every row, so a demo
 * where everyone is the same anonymous "You" quietly argues the opposite.
 */
function GuestForm({ onSignedIn }: { onSignedIn: () => void }) {
  const [name, setName] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await api.guestLogin(name)
      onSignedIn()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'sign-in failed')
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col">
      <DoorHeading note={<>No password, no account. Just a name to sign what you make.</>}>
        Look around
      </DoorHeading>
      <div className="mt-6" />
      <DoorLabel htmlFor="guest-name">Your name</DoorLabel>
      <input
        id="guest-name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Dana Reyes"
        autoComplete="name"
        autoFocus
        className="rounded-control text-lead w-full border px-3 py-2.5 outline-none"
        style={doorField}
      />
      <button
        type="submit"
        disabled={busy || !name.trim()}
        className="rounded-control text-body mt-2.5 w-full px-3 py-2.5 font-semibold disabled:opacity-50"
        style={doorButton}
      >
        {busy ? 'Opening…' : 'Open the demo'}
      </button>
      <DoorFinePrint>
        Everything in here is invented, and the whole log is emptied and reseeded every night.
      </DoorFinePrint>
      {error ? (
        <p className="text-meta mt-3" style={{ color: 'var(--crit)' }}>
          {error}
        </p>
      ) : null}
    </form>
  )
}
